import Day from "./Day";

export function relativeDate(
  {
    ms,
    seconds,
    minutes,
    hours,
    days,
    weeks,
    months,
    years,
  }: {
    ms?: number;
    seconds?: number;
    minutes?: number;
    hours?: number;
    days?: number;
    weeks?: number;
    months?: number;
    years?: number;
  },
  reference?: Date,
) {
  const date = reference ? new Date(reference.getTime()) : new Date();

  if (typeof ms !== "undefined" && ms) date.setMilliseconds(date.getMilliseconds() + ms);
  if (typeof seconds !== "undefined" && seconds) date.setSeconds(date.getSeconds() + seconds);
  if (typeof minutes !== "undefined" && minutes) date.setMinutes(date.getMinutes() + minutes);
  if (typeof hours !== "undefined" && hours) date.setHours(date.getHours() + hours);
  if (typeof days !== "undefined" && days) date.setDate(date.getDate() + days);
  if (typeof weeks !== "undefined" && weeks) date.setDate(date.getDate() + weeks * 7);

  const currentDay = date.getDate();
  let targetYear = date.getFullYear();
  let targetMonth = date.getMonth();

  const hasYearChange = typeof years !== "undefined" && years !== 0;
  const hasMonthChange = typeof months !== "undefined" && months !== 0;

  let newDate = date;

  if (hasYearChange || hasMonthChange) {
    const totalMonths = targetMonth + (hasMonthChange ? months : 0);
    targetYear += hasYearChange ? years : 0;
    targetYear += Math.floor(totalMonths / 12);
    targetMonth = ((totalMonths % 12) + 12) % 12;

    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const clampedDay = Math.min(currentDay, lastDay);

    newDate = new Date(
      Date.UTC(
        targetYear,
        targetMonth,
        clampedDay,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds(),
      ),
    );
  }

  return newDate;
}

export function durationInMilliseconds({
  seconds,
  minutes,
  hours,
  days,
}: {
  seconds?: number;
  minutes?: number;
  hours?: number;
  days?: number;
}) {
  return 1000 * durationInSeconds({ seconds, minutes, hours, days });
}

export function durationInSeconds({
  seconds,
  minutes,
  hours,
  days,
}: {
  seconds?: number;
  minutes?: number;
  hours?: number;
  days?: number;
}) {
  let duration = 0;
  if (typeof seconds !== "undefined" && seconds) duration += seconds;

  if (typeof minutes !== "undefined" && minutes) duration += minutes * 60;

  if (typeof hours !== "undefined" && hours) duration += hours * 60 * 60;

  if (typeof days !== "undefined" && days) duration += days * 60 * 60 * 24;

  return duration;
}

export function minDate(...dates: Array<Date | undefined | null>) {
  return new Date(Math.min(...dates.filter(Boolean).map((date) => date.getTime())));
}

export function maxDate(...dates: Array<Date | undefined | null>) {
  return new Date(Math.max(...dates.filter(Boolean).map((date) => date.getTime())));
}

export function minDay(...days: Array<Day | undefined | null>) {
  return days.filter(Boolean).sort((a, b) => a.getTime() - b.getTime())[0];
}

export function maxDay(...days: Array<Day | undefined | null>) {
  return days.filter(Boolean).sort((a, b) => b.getTime() - a.getTime())[0];
}

export function nextClockOccurrence(timeZone: string | null, hour: number, minute: number) {
  const clock = timeZone ? new Day().atTimezone(timeZone, hour, minute) : new Day().at(hour, minute);
  return clock < new Date() ? relativeDate({ days: 1 }, clock) : clock;
}
